/* eslint-disable no-restricted-imports */
import { useMemo } from 'react';

import { ConciergeTier, type Blueprint } from '@rtkwlf/track-public';
import { CISControl } from '@rtkwlf/track-public/src/models/CISControl';
import { NIST2Category } from '@rtkwlf/track-public/src/models/NIST2Category';
import { NISTCategory } from '@rtkwlf/track-public/src/models/NISTCategory';
import { SubscriptionType } from '@rtkwlf/track-public/src/models/SubscriptionType';
import { TechnicalDomain } from '@rtkwlf/track-public/src/models/TechnicalDomain';
import { Technology } from '@rtkwlf/track-public/src/models/Technology';
import { type TFunction } from 'i18next';

import { Box, Typography } from '@packages/fenrir/components';
import type {
  GridColDef,
  GridRenderCellParams,
} from '@packages/fenrir/components/datagrid';
import {
  DataGridPro,
  getFilteredGridSelectOperators,
  GridSelectOperator,
} from '@packages/fenrir/components/datagrid';
import { GridToolbar } from '@packages/fenrir/components/datagrid';

import { useSecurityJourneyTranslations } from '../../locales/use-security-journey-translations';

import { createOptions } from './_components/create-options';
import { getHaveAnyOfFilterOperator } from './_components/get-have-any-of-filter-operator';
import { ListCell } from './_components/list-cell';
import { TechnicalDomainsListCell } from './_components/technical-domains-list-cell';
import { TierCell } from './_components/tier-cell';

/* -------------------------------------------------------------------------------------------------
 * Utilities
 * -----------------------------------------------------------------------------------------------*/
type BlueprintRow = {
  id: string;
  bpId: string;
  name: string;
  tier: ConciergeTier;
  technicalDomains: string[];
  technologies: string[];
  nist: string[];
  nist2: string[];
  cis: string[];
  subscriptions: string[];
};

type ParseBlueprintsForTableParams = {
  /**
   * blueprints to transformed
   */
  blueprints: Blueprint[];
};

/**
 * Parses blueprints into the type that is required
 * for the blueprints table
 *
 * @param params of type `ParseBlueprintsForTableParams`
 */
const parseBlueprintsForTable = (
  params: ParseBlueprintsForTableParams,
): BlueprintRow[] => {
  if (params.blueprints.length === 0) return [];

  const rows = params.blueprints.map((blueprint, index): BlueprintRow => {
    const blueprintTechnicalDomains = new Set([
      blueprint.primaryTechnicalDomain,
    ]);

    if (blueprint.technicalDomains) {
      blueprint.technicalDomains.forEach((technicalDomain) =>
        blueprintTechnicalDomains.add(technicalDomain),
      );
    }

    return {
      id: crypto.randomUUID(),
      bpId: blueprint.id,
      name: blueprint.name,
      tier: blueprint.tier,
      technicalDomains: Array.from(blueprintTechnicalDomains),
      technologies: blueprint.technologies,
      nist: blueprint.nist,
      nist2: blueprint.nist2,
      cis: blueprint.cis,
      subscriptions: blueprint.subscriptions,
    };
  });

  return rows;
};

/* -------------------------------------------------------------------------------------------------
 * Columns
 * -----------------------------------------------------------------------------------------------*/
const BlueprintsTableColumn = {
  Name: 'name',
  Tier: 'tier',
  TechnicalDomains: 'technicalDomains',
  Technologies: 'technologies',
  Nist: 'nist',
  Nist2: 'nist2',
  Cis: 'cis',
  Subscriptions: 'subscriptions',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
type BlueprintsTableColumn =
  (typeof BlueprintsTableColumn)[keyof typeof BlueprintsTableColumn];

type ColumnDefinition = GridColDef<BlueprintRow>[];

type CustomCellProps = GridRenderCellParams<BlueprintRow, unknown> & {};

type GetColumnsOptions = {
  t: TFunction<'security-journey', undefined>;
};

const getColumns = ({ t }: GetColumnsOptions): ColumnDefinition => {
  const defaultColumns: GridColDef<BlueprintRow>[] = [
    {
      field: BlueprintsTableColumn.Name,
      headerName: t('common.blueprintsTable.headers.name'),
      minWidth: 300,
      resizable: true,
      display: 'flex',
    },
    {
      field: BlueprintsTableColumn.Tier,
      headerName: t('common.blueprintsTable.headers.tier'),
      width: 150,
      resizable: false,
      renderCell: TierCell,
      type: 'singleSelect',
      valueOptions: [
        { value: ConciergeTier._1, label: 'Silver' },
        { value: ConciergeTier._2, label: 'Gold' },
        { value: ConciergeTier._3, label: 'Platinum' },
      ],
      filterOperators: getFilteredGridSelectOperators([
        GridSelectOperator.isAnyOf,
      ]),
    },
    {
      field: BlueprintsTableColumn.TechnicalDomains,
      headerName: t('common.blueprintsTable.headers.technicalDomains'),
      minWidth: 200,
      resizable: true,
      sortable: false,
      display: 'flex',
      renderCell: (props) => {
        return (
          <TechnicalDomainsListCell
            technicalDomains={props.row.technicalDomains}
          />
        );
      },
      filterOperators: getHaveAnyOfFilterOperator(
        createOptions(Object.values(TechnicalDomain)),
      ),
    },
    {
      field: BlueprintsTableColumn.Technologies,
      headerName: t('common.blueprintsTable.headers.technologies'),
      minWidth: 200,
      resizable: true,
      sortable: false,
      display: 'flex',
      renderCell: (params) => <ListCell value={params.row.technologies} />,
      filterOperators: getHaveAnyOfFilterOperator(
        createOptions(Object.values(Technology)),
      ),
    },
    {
      field: BlueprintsTableColumn.Nist,
      headerName: t('common.blueprintsTable.headers.nist'),
      minWidth: 200,
      resizable: true,
      sortable: false,
      display: 'flex',
      renderCell: (params) => <ListCell value={params.row.nist} />,
      filterOperators: getHaveAnyOfFilterOperator(
        createOptions(Object.values(NISTCategory)),
      ),
    },
    {
      field: BlueprintsTableColumn.Nist2,
      headerName: t('common.blueprintsTable.headers.nist2'),
      minWidth: 200,
      resizable: true,
      sortable: false,
      display: 'flex',
      renderCell: (params) => <ListCell value={params.row.nist2} />,
      filterOperators: getHaveAnyOfFilterOperator(
        createOptions(Object.values(NIST2Category)),
      ),
    },
    {
      field: BlueprintsTableColumn.Cis,
      headerName: t('common.blueprintsTable.headers.cis'),
      minWidth: 200,
      resizable: true,
      sortable: false,
      display: 'flex',
      renderCell: (params) => <ListCell value={params.row.cis} />,
      filterOperators: getHaveAnyOfFilterOperator(
        createOptions(Object.values(CISControl)),
      ),
    },
    {
      field: BlueprintsTableColumn.Subscriptions,
      headerName: t('common.blueprintsTable.headers.subscriptions'),
      minWidth: 200,
      resizable: true,
      sortable: false,
      display: 'flex',
      renderCell: (params) => <ListCell value={params.row.subscriptions} />,
      filterOperators: getHaveAnyOfFilterOperator(
        createOptions(Object.values(SubscriptionType)),
      ),
    },
  ] satisfies ColumnDefinition;

  return defaultColumns;
};

/* -------------------------------------------------------------------------------------------------
 * BlueprintsTable
 * -----------------------------------------------------------------------------------------------*/
type BlueprintsTableProps = {
  /**
   * blueprints to be displayed
   */
  blueprints: BlueprintRow[];

  /**
   * columns to be displayed
   */
  tableColumns: BlueprintsTableColumn[];

  /**
   * the title and description of when no data is available
   * defaults to 'No SPiDR Blueprints'
   */
  emptyResultMessage?: string;
};

const BlueprintsTable = (tableProps: BlueprintsTableProps) => {
  const { blueprints, tableColumns, emptyResultMessage } = tableProps;

  const { t } = useSecurityJourneyTranslations();

  /**
   * compute the columns for the table
   */
  const columns = useMemo(() => {
    let tempColumns = getColumns({
      t,
    });

    tempColumns = tempColumns.filter((column) =>
      tableColumns.includes(column.field as BlueprintsTableColumn),
    );

    return tempColumns;
  }, [t, tableColumns]);

  const slots = {
    toolbar: () => (
      <GridToolbar
        showQuickFilter
        quickFilterProps={{
          placeholder: t(
            'common.blueprintsTable.filters.quickFilter.placeholder',
          ),
        }}
      />
    ),
    noRowsOverlay: () => (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <Typography variant="body1">{emptyResultMessage}</Typography>
      </Box>
    ),
  };

  return (
    <DataGridPro
      rows={blueprints}
      columns={columns}
      getRowId={(row: BlueprintRow) => row.id}
      slots={slots}
      hideFooter
      disableRowSelectionOnClick
      disableDensitySelector
      getRowHeight={() => 'auto'}
    />
  );
};

export { BlueprintsTable, parseBlueprintsForTable, BlueprintsTableColumn };
export type { BlueprintsTableProps, BlueprintRow, CustomCellProps };
